/**
 * Duplicate Store troubleshooting page: triggers a new store duplication (target + source +
 * stale-entity mode) and lists every duplication attempt as an expandable table of actions,
 * with per-action filtering and GitHub-style before/after JSON diffs.
 */
import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { SingleValue } from 'react-select';

import {
    IStore,
    STALE_ENTITY_MODE_VALUES,
    StaleEntityMode,
    STORE_COPY_STATUS_VALUES,
    StoreCopyStatus,
} from '../../../../services/api/troubleshooting-service';
import CurrentPortalStore from '../../../../stores/current-portal-store';
import { IOption, SelectDropdown } from '../../../../components/inputs/select-dropdown/select-dropdown';
import { Input } from '../../../../components/inputs/input/input';
import { Button } from '../../../../components/inputs/button/button';
import { ConfirmModal } from '../../../../components/modal/components/confirm-modal/confirm-modal';
import { FONT_COLOR, FONT_VARIANT } from '../../../../components/fonts/font-props';
import FontBody2 from '../../../../components/fonts/font-body2/font-body2';
import LinearLoader from '../../../../components/loaders/linear-loader/linear-loader';
import Pagination from '../../../../components/table/components/pagination/pagination';

import TroubleshootingStore from '../../troubleshooting-page-store';
import DuplicateStorePageStore from './duplicate-store-page-store';
import AttemptsTable from './components/attempts-table';

import './duplicate-store-page.scss';

const STALE_ENTITY_MODE_LABELS: Record<StaleEntityMode, string> = {
    soft_delete: 'Soft delete',
    disable: 'Disable',
    leave: 'Leave',
};

/**
 * "Stale" entities are records already on the target store that don't exist on the source.
 * This controls what happens to them after the copy.
 */
const STALE_ENTITY_MODE_DESCRIPTIONS: Record<StaleEntityMode, string> = {
    soft_delete: 'Soft-deletes target entities that aren’t on the source (recoverable).',
    disable: 'Keeps target entities that aren’t on the source but turns them off.',
    leave: 'Leaves target entities that aren’t on the source untouched.',
};

const STALE_ENTITY_MODE_SELECT_OPTIONS: IOption[] = STALE_ENTITY_MODE_VALUES.map((mode) => ({
    value: mode,
    label: STALE_ENTITY_MODE_LABELS[mode],
    description: STALE_ENTITY_MODE_DESCRIPTIONS[mode],
}));

const STATUS_FILTER_LABELS: Record<StoreCopyStatus, string> = {
    pending: 'Pending',
    processing: 'Processing',
    completed: 'Completed',
    failed: 'Failed',
};

const ALL_STATUSES_OPTION: IOption = { value: '', label: 'All statuses' };

const STATUS_FILTER_OPTIONS: IOption[] = [
    ALL_STATUSES_OPTION,
    ...STORE_COPY_STATUS_VALUES.map((status) => ({ value: status, label: STATUS_FILTER_LABELS[status] })),
];

const ATTEMPTS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const DuplicateStorePage: React.FC = observer(() => {
    const [targetStore, setTargetStore] = useState<IOption | null>(null);
    const [sourceStore, setSourceStore] = useState<IOption | null>(null);
    const [staleModeOption, setStaleModeOption] = useState<IOption>(STALE_ENTITY_MODE_SELECT_OPTIONS[0]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [triggerResult, setTriggerResult] = useState<string | null>(null);
    const [triggerError, setTriggerError] = useState<string | null>(null);

    const [searchInput, setSearchInput] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [statusOption, setStatusOption] = useState<IOption>(ALL_STATUSES_OPTION);
    const [page, setPage] = useState(1);

    useEffect(() => {
        TroubleshootingStore.fetchStores();
        CurrentPortalStore.load();
    }, []);

    // Debounce the free-text store search; reset to the first page when it changes.
    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedSearch(searchInput.trim());
            setPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const fetchAttempts = () => {
        void DuplicateStorePageStore.fetchAttempts({
            storeSearch: appliedSearch || undefined,
            status: (statusOption.value as StoreCopyStatus) || undefined,
            page,
            perPage: ATTEMPTS_PER_PAGE,
        });
    };

    useEffect(() => {
        fetchAttempts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedSearch, statusOption.value, page]);

    const portalId = CurrentPortalStore.currentPortal.id;
    const portalName = CurrentPortalStore.currentPortal.name;
    const portalDisplayName = CurrentPortalStore.currentPortal.displayName;

    useEffect(() => {
        if (targetStore?.value !== portalId && portalId && portalName) {
            setTargetStore({
                value: portalId,
                label: portalName + (portalDisplayName ? ' (' + portalDisplayName + ')' : ''),
            });
        }
    }, [portalId, portalName, portalDisplayName, targetStore?.value]);

    const storeOptions = useMemo((): IOption[] => {
        return TroubleshootingStore.stores?.map((el: IStore): IOption => ({
            value: el.id,
            label: el.name + (el.display_name ? ' (' + el.display_name + ')' : ''),
        })) || [];
    }, [TroubleshootingStore.stores]);

    const canDuplicate = !!targetStore?.value && !!sourceStore?.value;

    const confirmDuplicate = async () => {
        if (!targetStore?.value || !sourceStore?.value) return;
        setShowConfirm(false);
        setTriggerResult(null);
        setTriggerError(null);
        try {
            const response = await DuplicateStorePageStore.triggerDuplicate(
                targetStore.value,
                sourceStore.value,
                staleModeOption.value as StaleEntityMode,
            );
            const message = response?.data?.message ?? 'Duplicate store job dispatched.';
            if (response?.data?.status === 'failed') {
                setTriggerError(message);
            } else {
                setTriggerResult(message);
            }
        } catch (err: any) {
            setTriggerError(err?.response?.data?.error ?? err?.message ?? 'Request failed');
        } finally {
            fetchAttempts();
        }
    };

    const { attempts, isLoading, error, pagination } = DuplicateStorePageStore;

    return (
        <div className="duplicate-store-page">
            <div className="card">
                <h4>Duplicate a store</h4>
                <div className="controls">
                    <p className="head5">Target store</p>
                    <SelectDropdown
                        required
                        value={targetStore || undefined}
                        placeholder="Select the store to duplicate into"
                        options={storeOptions}
                        onChange={(val: SingleValue<IOption>) => setTargetStore(val || null)}
                    />
                </div>
                <div className="controls">
                    <p className="head5">Source store</p>
                    <SelectDropdown
                        required
                        value={sourceStore || undefined}
                        placeholder="Select the store to copy configuration from"
                        options={storeOptions}
                        onChange={(val: SingleValue<IOption>) => setSourceStore(val || null)}
                    />
                </div>
                <div className="controls">
                    <p className="head5">Stale entity mode</p>
                    <SelectDropdown
                        required
                        value={staleModeOption}
                        placeholder="Select stale entity mode"
                        options={STALE_ENTITY_MODE_SELECT_OPTIONS}
                        onChange={(val: SingleValue<IOption>) => { if (val) setStaleModeOption(val); }}
                    />
                </div>
                <Button
                    disabled={!canDuplicate}
                    text="Duplicate store"
                    onClick={() => setShowConfirm(true)}
                />

                {triggerResult && (
                    <FontBody2 color={FONT_COLOR.SEMANTIC_SUCCESS} variant={FONT_VARIANT.BOLD}>
                        {triggerResult}
                    </FontBody2>
                )}
                {triggerError && (
                    <FontBody2 color={FONT_COLOR.SEMANTIC_DANGER} variant={FONT_VARIANT.BOLD}>
                        {triggerError}
                    </FontBody2>
                )}
            </div>

            <div className="card results-card">
                <div className="results-card__header">
                    <h4>Duplication attempts</h4>
                    <Button text="Refresh" onClick={fetchAttempts} />
                </div>

                <div className="results-card__filters">
                    <Input
                        type="search"
                        placeholder="Search by store name, store ID or Global-e user ID"
                        value={searchInput}
                        onChange={(val: string) => setSearchInput(val)}
                    />
                    <SelectDropdown
                        value={statusOption}
                        options={STATUS_FILTER_OPTIONS}
                        placeholder="Filter by status"
                        onChange={(val: SingleValue<IOption>) => {
                            setStatusOption(val || ALL_STATUSES_OPTION);
                            setPage(1);
                        }}
                    />
                </div>

                {isLoading && <LinearLoader />}

                {error && !isLoading && (
                    <FontBody2 color={FONT_COLOR.SEMANTIC_DANGER} variant={FONT_VARIANT.BOLD}>
                        {error}
                    </FontBody2>
                )}

                {!isLoading && !error && attempts.length === 0 && (
                    <FontBody2 color={FONT_COLOR.PRIMARY2}>
                        {appliedSearch || statusOption.value
                            ? 'No attempts match the current filters.'
                            : 'No duplication attempts yet.'}
                    </FontBody2>
                )}

                {!isLoading && !error && attempts.length > 0 && (
                    <>
                        <AttemptsTable attempts={attempts} />
                        {pagination && (
                            <Pagination
                                page={pagination.current_page}
                                perPage={pagination.per_page}
                                total={pagination.total}
                                totalResultsTitle="attempts"
                                onSetPage={setPage}
                            />
                        )}
                    </>
                )}
            </div>

            {showConfirm && (
                <ConfirmModal
                    title="Duplicate Store"
                    text=""
                    implicationText={<>Note: this action will duplicate the full configuration from <strong>"{sourceStore?.label ?? sourceStore?.value}"</strong> to <strong>"{targetStore?.label ?? targetStore?.value}"</strong> and <mark><em>may overwrite the target store settings</em>.</mark></>}
                    questionText="Are you sure you want to duplicate the store?"
                    cancelButton={{ title: 'Cancel', onClick: () => setShowConfirm(false) }}
                    okButton={{ title: 'Confirm', onClick: confirmDuplicate }}
                />
            )}
        </div>
    );
});

export default DuplicateStorePage;
